import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { 
  Lock, 
  Loader2, 
  Calendar, 
  Clock, 
  MapPin, 
  Phone, 
  Mail, 
  User,
  CheckCircle2,
  XCircle,
  Clock3,
  BarChart3,
  LogOut,
  RefreshCw
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPage = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [stats, setStats] = useState(null);
  const [filter, setFilter] = useState('all');
  const [loadingBookings, setLoadingBookings] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('ba_admin_token');
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      fetchData();
    }
  }, []);

  const fetchData = async () => {
    setLoadingBookings(true);
    try {
      const [bookingsRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/bookings`),
        axios.get(`${API}/admin/stats`)
      ]);
      setBookings(bookingsRes.data);
      setStats(statsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      if (error.response?.status === 401 || error.response?.status === 403) {
        handleLogout();
      }
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoadingBookings(false);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      const { token } = response.data;
      localStorage.setItem('ba_admin_token', token);
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      setIsAuthenticated(true);
      toast.success('Connexion réussie');
      fetchData();
    } catch (error) {
      console.error('Login error:', error);
      toast.error('Mot de passe incorrect');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('ba_admin_token');
    delete axios.defaults.headers.common['Authorization'];
    setIsAuthenticated(false);
    setBookings([]);
    setStats(null);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}`, { status: newStatus });
      toast.success('Statut mis à jour');
      fetchData();
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const configs = {
      pending: { label: 'En attente', variant: 'warning', icon: Clock3 },
      confirmed: { label: 'Confirmée', variant: 'info', icon: CheckCircle2 },
      completed: { label: 'Terminée', variant: 'success', icon: CheckCircle2 },
      cancelled: { label: 'Annulée', variant: 'destructive', icon: XCircle },
    };
    const config = configs[status] || { label: status, variant: 'secondary' };
    return (
      <Badge 
        variant={config.variant === 'warning' ? 'outline' : config.variant === 'info' ? 'secondary' : config.variant}
        className={`${
          config.variant === 'warning' ? 'border-amber-500 text-amber-600 bg-amber-50' :
          config.variant === 'info' ? 'border-sky-500 text-sky-600 bg-sky-50' :
          config.variant === 'success' ? 'border-green-500 text-green-600 bg-green-50' :
          ''
        }`}
      >
        {config.label}
      </Badge>
    );
  };

  const filteredBookings = filter === 'all' 
    ? bookings 
    : bookings.filter(b => b.status === filter);

  // Login Screen
  if (!isAuthenticated) {
    return (
      <div className="min-h-screen pt-16 md:pt-20 bg-slate-900 flex items-center justify-center px-4" data-testid="admin-login">
        <Card className="w-full max-w-md border-slate-700 bg-slate-800">
          <CardHeader className="text-center">
            <div className="h-24 overflow-hidden mx-auto mb-4 bg-white rounded-lg">
              <img 
                src="https://customer-assets.emergentagent.com/job_nettoyage-rdv/artifacts/sr58gnln_Screenshot_2025-11-19-09-02-07-924_com.miui.gallery.jpg" 
                alt="BA NETTOYAGE" 
                className="h-[200%] w-auto object-cover object-center -mt-[25%]"
              />
            </div>
            <CardTitle className="font-['Outfit'] text-2xl text-white">Panel Admin</CardTitle>
            <p className="text-slate-400 mt-2">BA NETTOYAGE</p>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <Label htmlFor="password" className="text-slate-300">Mot de passe admin</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="mt-2 h-12 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                  data-testid="admin-password"
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full rounded-full bg-sky-500 hover:bg-sky-600 h-12"
                data-testid="admin-login-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                    Connexion...
                  </>
                ) : (
                  'Se connecter'
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Admin Dashboard
  return (
    <div className="min-h-screen pt-16 md:pt-20 bg-slate-50" data-testid="admin-dashboard">
      {/* Header */}
      <section className="bg-slate-900 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-['Outfit'] text-2xl md:text-3xl font-bold text-white">
                Panel Admin
              </h1>
              <p className="text-slate-400 mt-1">Gestion des réservations</p>
            </div>
            <div className="flex items-center gap-3">
              <Button
                variant="outline"
                size="sm"
                onClick={fetchData}
                disabled={loadingBookings}
                className="border-slate-600 text-slate-300 hover:bg-slate-800"
                data-testid="refresh-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${loadingBookings ? 'animate-spin' : ''}`} />
                Actualiser
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleLogout}
                className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                data-testid="logout-btn"
              >
                <LogOut className="w-4 h-4 mr-2" />
                Déconnexion
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats */}
      {stats && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-6">
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
            <Card className="border-none shadow-lg">
              <CardContent className="p-4 text-center">
                <BarChart3 className="w-6 h-6 text-slate-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-slate-900">{stats.total}</p>
                <p className="text-slate-500 text-sm">Total</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-amber-50">
              <CardContent className="p-4 text-center">
                <Clock3 className="w-6 h-6 text-amber-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-amber-600">{stats.pending}</p>
                <p className="text-amber-600 text-sm">En attente</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-sky-50">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-6 h-6 text-sky-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-sky-600">{stats.confirmed}</p>
                <p className="text-sky-600 text-sm">Confirmées</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-green-50">
              <CardContent className="p-4 text-center">
                <CheckCircle2 className="w-6 h-6 text-green-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-green-600">{stats.completed}</p>
                <p className="text-green-600 text-sm">Terminées</p>
              </CardContent>
            </Card>
            <Card className="border-none shadow-lg bg-red-50">
              <CardContent className="p-4 text-center">
                <XCircle className="w-6 h-6 text-red-500 mx-auto mb-2" />
                <p className="text-2xl font-bold text-red-600">{stats.cancelled}</p>
                <p className="text-red-600 text-sm">Annulées</p>
              </CardContent>
            </Card>
          </div>
        </div>
      )}

      {/* Bookings Table */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20">
        <Card className="border-none shadow-lg">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="font-['Outfit'] text-xl">Réservations</CardTitle>
            <Select value={filter} onValueChange={setFilter}>
              <SelectTrigger className="w-[180px]" data-testid="filter-select">
                <SelectValue placeholder="Filtrer par statut" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Toutes</SelectItem>
                <SelectItem value="pending">En attente</SelectItem>
                <SelectItem value="confirmed">Confirmées</SelectItem>
                <SelectItem value="completed">Terminées</SelectItem>
                <SelectItem value="cancelled">Annulées</SelectItem>
              </SelectContent>
            </Select>
          </CardHeader>
          <CardContent>
            {loadingBookings ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-sky-500" />
              </div>
            ) : filteredBookings.length === 0 ? (
              <div className="text-center py-12">
                <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                <p className="text-slate-500">Aucune réservation</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Date / Heure</TableHead>
                      <TableHead>Adresse</TableHead>
                      <TableHead>Statut</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredBookings.map((booking) => (
                      <TableRow key={booking.id} data-testid={`booking-row-${booking.id}`}>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2 font-medium">
                              <User className="w-4 h-4 text-slate-400" />
                              {booking.user_name}
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Phone className="w-3 h-3" />
                              <a href={`tel:${booking.user_phone}`} className="hover:text-sky-500">
                                {booking.user_phone}
                              </a>
                            </div>
                            <div className="flex items-center gap-2 text-sm text-slate-500">
                              <Mail className="w-3 h-3" />
                              <a href={`mailto:${booking.user_email}`} className="hover:text-sky-500">
                                {booking.user_email}
                              </a>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <span className="font-medium text-sky-600">{booking.service_name}</span>
                          {booking.notes && (
                            <p className="text-xs text-slate-500 mt-1 max-w-[150px] truncate" title={booking.notes}>
                              Note: {booking.notes}
                            </p>
                          )}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            <div className="flex items-center gap-2">
                              <Calendar className="w-4 h-4 text-slate-400" />
                              {format(new Date(booking.date), 'd MMM yyyy', { locale: fr })}
                            </div>
                            <div className="flex items-center gap-2 text-slate-500">
                              <Clock className="w-4 h-4" />
                              {booking.time}
                            </div>
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-start gap-2 max-w-[200px]">
                            <MapPin className="w-4 h-4 text-slate-400 mt-0.5 flex-shrink-0" />
                            <span className="text-sm">
                              {booking.address}, {booking.postal_code} {booking.city}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell>
                          {getStatusBadge(booking.status)}
                        </TableCell>
                        <TableCell>
                          <Select
                            value={booking.status}
                            onValueChange={(value) => handleStatusUpdate(booking.id, value)}
                          >
                            <SelectTrigger className="w-[140px]" data-testid={`status-select-${booking.id}`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="pending">En attente</SelectItem>
                              <SelectItem value="confirmed">Confirmée</SelectItem>
                              <SelectItem value="completed">Terminée</SelectItem>
                              <SelectItem value="cancelled">Annulée</SelectItem>
                            </SelectContent>
                          </Select>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPage;
